import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";

import { PrimaryButton } from '../../components/PrimaryButton';
import LoadingLogo from '../../components/LoadingLogo';
import GenericCard from './GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const Utilisateurs = () => {
  const { t } = useTranslation();
  const topRef = useRef(null);

  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [filters, setFilters] = useState({ needle: "", roleId: "" });

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showModule, setShowModule] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = 'success', manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const roleKeys = Object.keys(t('roles', { returnObjects: true }) || {});
  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "roleId",
      label: t(`filters.users.role`),
      type: "dropdown",
      options: roleKeys.map((roleKey) => ({
        value: roleKey,
        label: t(`roles.${roleKey}`),
      })),
    },
  ];

  const fetchPaginatedUsers = useCallback(
    async (page = currentPage, size = pageSize, active = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));

        const n = (active.needle || "").trim();
        if (n) params.set('needle', n);

        const r = (active.roleId || "").trim();
        if (r) params.set('role', r);

        const res = await secureGet(`/users?${params.toString()}`, showSnackbar, { signal });
        const data = res?.data ?? {};

        const content = Array.isArray(data.content) ? data.content : Array.isArray(data) ? data : [];
        setUsers(content);
        setTotalPages(Number.isFinite(data.totalPages) ? data.totalPages : 1);

        if (Number.isFinite(data.number)) setCurrentPage(data.number);
        if (Number.isFinite(data.size)) setPageSize(data.size);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching users:', error);
          setUsers([]);
          setTotalPages(1);
        }
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, filters, signal]
  );

  useEffect(() => {
    fetchPaginatedUsers(0, pageSize, { needle: "", roleId: "" });
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedUsers(page, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedUsers(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updated = { ...filters, [filterName]: filterValue };
    setFilters(updated);
    setCurrentPage(0);
    fetchPaginatedUsers(0, pageSize, updated);
  };

  const resetFilters = () => {
    const reset = { needle: "", roleId: "" };
    setFilters(reset);
    setCurrentPage(0);
    fetchPaginatedUsers(0, pageSize, reset);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/users/${itemToDelete.id}`, showSnackbar);
      await fetchPaginatedUsers(currentPage, pageSize, filters);
      showSnackbar(t("confirmation.userDelete"), "success");
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleAddClick = () => {
    setSelectedUser(null);
    setShowModule(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setShowModule(true);
  };

  const getMenuOptions = (user) => ([
    { label: t(`actions.modify`), icon: EditIcon, action: () => handleEditClick(user) },
    { label: t(`actions.delete`), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(user) },
  ]);

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") fetchPaginatedUsers(currentPage, pageSize, filters);
    setShowModule(false);
    setSelectedUser(null);
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.users")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.users`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_user`)}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      {/* Filters */}
      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      {/* Content with loading overlay */}
      <div style={{ position: "relative", minHeight: 200 }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        {/* Cards */}
        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && users.length > 0 ? (
            users.map((user) => (
              <GenericCard
                key={user.id}
                title={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                subtitle={
                  user.repoId
                    ? `${t(`roles.${user.role}`)} - ${user.repoName}`
                    : t(`roles.${user.role}`)
                }
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>
                    <OptionsMenu options={getMenuOptions(user)} />
                  </div>
                }
                statusTag={
                  user.status ? (
                    <span
                      className={`px-2 py-1 rounded-full text-sm font-medium ${
                        user.status === 'active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                      }`}
                    >
                      {user.status}
                    </span>
                  ) : null
                }
                additionalFields={user.additionalFields || []}
              />
            ))
          ) : (
            !loading && (
              <div className="text-center mt-4 col-span-full">
              </div>
            )
          )}
        </div>
      </div>

      {/* Pagination */}
      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Delete */}
      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Module */}
      {showModule && (
        <ModuleWrapper
          moduleType="users"
          mode={selectedUser ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedUser || {}}
        />
      )}

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
}

export default Utilisateurs;
